// ============================================
// SESION.JS — Manejo global de sesion expirada
// ============================================
// FIX-SESION-OBSERVABILIDAD: cuando el token caduca, el backend devuelve 401.
// Antes esto se tragaba silenciosamente (catch { /* ignorar */ }) y la UI quedaba
// muda — clicks no respondian, menu vacio, sin error visible. Patron de banco:
// detectar 401, avisar al usuario, y redirigir al login.
//
// Uso:
//   1. fetchConSesion(url, options) — wrapper de fetch que detecta 401
//      automaticamente, borra la sesion y redirige a /beep-login. Lanza un Error
//      con .code='SESION_EXPIRADA' para que el caller no procese una respuesta
//      invalida.
//   2. manejarSesionExpirada() — accion directa para llamar cuando detectes una
//      sesion inconsistente sin pasar por fetch (ej: token faltante en cliente).
//
// Bloque inicial: solo se usa en el menu (Dashboard.js). El resto del sistema
// puede migrarse en bloques sucesivos sin urgencia.

import { STORAGE_KEYS, storage, authHeaders } from './storage';
// VER-COMO: restauracion canonica de la sesion original del director. Import
// unidireccional y seguro (verComo.js solo importa de './storage' — sin ciclo).
import { restaurarSesionOriginal } from './verComo';

// Flag ephemera (sessionStorage, no localStorage) que Login.js lee al montar
// para avisar al usuario que su sesion expiro. Se autolimpia tras mostrarlo.
const FLAG_SESION_EXPIRO = 'beep_sesion_expiro';

// Borra el estado de sesion del cliente, levanta el flag y redirige al login.
// Idempotente: si ya estamos en /beep-login no redirige (evita loop).
// VER-COMO: si el 401 llego porque expiro un token de impersonacion, NO se
// desloguea al director — se restaura su sesion original con un aviso.
export function manejarSesionExpirada() {
  try {
    const backup = storage.get(STORAGE_KEYS.VER_COMO_BACKUP);
    if (backup && backup.token) {
      // Un solo camino: la restauracion vive en utils/verComo.js.
      restaurarSesionOriginal('expiro');
      return;
    }
  } catch (e) {
    console.error('[manejarSesionExpirada] error restaurando Ver como:', e);
  }
  try {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    storage.remove(STORAGE_KEYS.USER_DATA);
    storage.remove(STORAGE_KEYS.USER_CONTEXT_CACHE);
    try { sessionStorage.setItem(FLAG_SESION_EXPIRO, '1'); } catch (_) {}
  } catch (e) {
    // Si algo falla limpiando, igual queremos redirigir.
    console.error('[manejarSesionExpirada] error limpiando estado:', e);
  }
  try {
    if (typeof window !== 'undefined' && window.location.pathname !== '/beep-login') {
      window.location.href = '/beep-login';
    }
  } catch (_) {}
}

// Lee y limpia el flag. Login.js llama esto al montar.
export function consumirFlagSesionExpiro() {
  try {
    const v = sessionStorage.getItem(FLAG_SESION_EXPIRO);
    if (v) sessionStorage.removeItem(FLAG_SESION_EXPIRO);
    return v === '1';
  } catch (_) { return false; }
}

// Wrapper de fetch que detecta 401 (token invalido/expirado) y dispara el
// manejo global. Lanza Error con .code='SESION_EXPIRADA' para frenar al caller.
//
// SESION-2: ademas detecta el header X-Token-Renovado (sliding session). Si el
// backend renovo el token, lo guardamos en localStorage de forma transparente.
// El usuario activo NUNCA pierde sesion mientras siga haciendo requests.
//
// Si la red falla (no llega al servidor), el error original se propaga sin
// disparar manejarSesionExpirada (eso seria perder la sesion por un wifi caido).
export async function fetchConSesion(url, options) {
  const resp = await fetch(url, options);
  if (resp.status === 401) {
    manejarSesionExpirada();
    const err = new Error('Sesion expirada');
    err.code = 'SESION_EXPIRADA';
    throw err;
  }
  try {
    const nuevoToken = resp.headers.get('X-Token-Renovado');
    // VER-COMO: si hay modo activo, NO pisar el token. Cubre la carrera de una
    // peticion del director en vuelo cuya renovacion llega DESPUES de que
    // iniciarVerComo instalo el token de impersonacion (veto logica-negocio).
    if (nuevoToken && !storage.get(STORAGE_KEYS.VER_COMO_META)) {
      storage.set(STORAGE_KEYS.AUTH_TOKEN, nuevoToken);
    }
  } catch (e) {
    // No bloquear el flujo si falla la lectura del header.
    console.error('[fetchConSesion] no se pudo leer X-Token-Renovado:', e);
  }
  return resp;
}

// ============================================
// PERM-4 CAPA 1: fetchAuth — wrapper que inyecta el token automaticamente.
// ============================================
// Uso recomendado para CUALQUIER llamada que requiera sesion (escritura o lectura
// protegida):
//   const r = await fetchAuth('/api/cotizaciones/generar', { method: 'POST', body });
//
// Que hace:
//   1. Inyecta el header Authorization: Bearer <token> leyendo STORAGE_KEYS.AUTH_TOKEN
//      (via authHeaders() de F1.B). Si el caller ya puso un Authorization propio, se
//      respeta el del caller.
//   2. Preserva cualquier header que ya traiga options (Content-Type, etc.).
//   3. Pasa por fetchConSesion, asi que ademas hereda el manejo de 401 (sesion
//      expirada -> redirige a login) y la renovacion sliding-session.
//
// Razon: antes cada fetch armaba el header a mano y era facil olvidarlo. Con esto
// el token viaja siempre y el problema no se repite. NO cambia la logica de negocio.
export async function fetchAuth(url, options = {}) {
  const headers = { ...authHeaders(), ...(options.headers || {}) };
  return fetchConSesion(url, { ...options, headers });
}
